/**
 * The local database. This — not the server — is what the UI reads from.
 *
 * That rule is the whole offline design in one sentence: no screen ever awaits
 * a network call to render. A phone in a basement gym behaves exactly like one
 * on wifi, because as far as the UI is concerned there is no difference. The
 * network only ever moves data *between* this store and the server, in the
 * background, via the outbox.
 *
 * Dexie (IndexedDB) rather than Capacitor SQLite, deliberately: it behaves
 * identically in desktop Chrome and the Android WebView, so there is one data
 * layer to maintain rather than two. The volume argues the same way — two
 * years of hard training is roughly 15,000 set rows, a couple of megabytes.
 */

import Dexie, { type Table } from 'dexie'

import type { DistanceScale } from '../lib/units'

// --- row types --------------------------------------------------------------
// These mirror the server's schema. Weight is ALWAYS kilograms; unit_pref is a
// display concern only. Mixed-unit storage is a bug factory that surfaces
// months later inside an aggregate nobody thinks to check.

export type Session = {
  id: string
  plan_id?: number | null
  plan_day_id?: number | null
  title?: string | null
  started_at: string
  finished_at?: string | null
  notes?: string | null
  perceived_fatigue?: number | null
  bodyweight_kg?: number | null
  updated_at: string
  deleted_at?: string | null
  seq: number
}

export type SetRow = {
  id: string
  session_id: string
  exercise_id: number
  performed_at: string
  order_index: number
  set_index: number
  weight_kg?: number | null
  reps?: number | null
  duration_seconds?: number | null
  distance_m?: number | null
  rpe?: number | null
  is_warmup: boolean
  is_failure: boolean
  /** Server-derived. Present on pulled rows, absent until a local row syncs. */
  e1rm?: number | null
  /**
   * Server-derived. The load, when it isn't `weight_kg` — assist machines only,
   * null everywhere else. Read it through `movedWeightKg()`.
   */
  effective_weight_kg?: number | null
  notes?: string | null
  /**
   * 'L' or 'R' on one side of a one-sided exercise's set; the two rows sharing
   * a set_index are one set. Absent or null everywhere else, including all
   * history from before sides existed. Count sets through lib/rounds.
   */
  side?: 'L' | 'R' | null
  updated_at: string
  deleted_at?: string | null
  seq: number
}

export type Metric = {
  id: string
  measured_at: string
  weight_kg?: number | null
  bodyfat_pct?: number | null
  measurements_json?: string | null
  notes?: string | null
  updated_at: string
  deleted_at?: string | null
  seq: number
}

export type Exercise = {
  id: number
  owner_id: number | null
  name: string
  slug: string
  category: string
  primary_muscle: string | null
  equipment: string
  is_unilateral: boolean
  is_bodyweight: boolean
  /**
   * The stack SUBTRACTS: `weight_kg` is the help taken off, not added to, the
   * body weight. Optional because a row cached by an older client won't carry
   * it — read it through `weightIsAssist()`, never directly.
   */
  is_assisted?: boolean | null
  /**
   * How much of the lifter is part of the load, 0..1. Zero (and absent, on a
   * row cached by an older client) means the load is exactly `weight_kg`; one
   * means a pull up, a chin up, a dip, or an assist machine. Read it through
   * `bodyweightFactor()`.
   */
  bodyweight_factor?: number | null
  /**
   * The load cannot be changed, so a one-rep max is undefined — the jumps.
   * They carry a real load and real volume; the server simply stores no `e1rm`
   * for them and emits no heaviest-set record.
   */
  load_is_fixed?: boolean | null
  /**
   * The plate calculator can work this one out. False on an EZ bar, whose own
   * weight is not a constant, and on a landmine, which loads one end and so has
   * no "per side" at all. Absent on a row cached by an older client, which
   * reads as true — the behaviour those clients already have.
   */
  bar_is_standard?: boolean | null
  increment_kg: number
  /**
   * How to do it, for someone who has never done it. Absent on rows cached by
   * a client older than this column, and null for an exercise a user invented
   * — nobody wrote one. Both cases render the same way: no control at all,
   * rather than a control that opens on nothing.
   */
  description?: string | null
  /** Set when removed from the library. Still sent, so history can name it. */
  archived_at?: string | null
  /**
   * What a set of this exercise measures. Absent on rows cached by a client
   * older than this column, so every read goes through `measuresOf()` rather
   * than reading the field directly.
   */
  tracking?: Tracking | null
  /** Sent by the server so the mode -> inputs mapping isn't hardcoded here. */
  measures?: Measure[] | null
  /**
   * Whether this exercise's distance is metres or miles/km. Absent on rows
   * cached by an older client, so read it through `distanceScaleOf()`.
   */
  distance_scale?: DistanceScale | null
}

export type Measure = 'weight' | 'reps' | 'time' | 'distance'

export type Tracking =
  | 'weight_reps'
  | 'reps'
  | 'time'
  | 'weight_time'
  | 'distance_time'
  | 'weight_distance'

const MEASURES: Record<Tracking, Measure[]> = {
  weight_reps: ['weight', 'reps'],
  reps: ['reps', 'weight'],
  time: ['time'],
  weight_time: ['weight', 'time'],
  distance_time: ['distance', 'time'],
  weight_distance: ['weight', 'distance'],
}

/**
 * What to ask for when logging this exercise.
 *
 * Prefers the server's own answer, so adding a mode server-side doesn't need a
 * client release to render correctly. Falls back to the local table, then to
 * weight x reps — an exercise cached before this existed keeps working exactly
 * as it did rather than rendering an empty form.
 */
export function measuresOf(exercise: Exercise | null | undefined): Measure[] {
  if (exercise?.measures?.length) return exercise.measures
  const mode = exercise?.tracking
  return mode && MEASURES[mode] ? MEASURES[mode] : MEASURES.weight_reps
}

/**
 * Metres, or miles/km. Mirrors Exercise.distance_scale on the server.
 *
 * The fallback is not a guess: a loaded carry is short by its tracking mode, so
 * a client too old to have been sent the field still gets carries right, and
 * still gets runs right, and is only ever wrong about a sprint — which is the
 * smallest possible blast radius for a stale cache.
 */
export function distanceScaleOf(
  exercise: Exercise | null | undefined,
): DistanceScale {
  if (exercise?.distance_scale) return exercise.distance_scale
  return exercise?.tracking === 'weight_distance' ? 'short' : 'long'
}

/**
 * Does this exercise's weight field mean *added* load?
 *
 * `is_bodyweight` has been on every row since the first migration, seeded
 * correctly for all 189 exercises, sent to the client — and read by nothing.
 * A pull up's weight box said "Weight", identically to a bench press, so the
 * only honest answer to "what do I put here" was to leave it blank.
 */
export function weightIsAdded(exercise: Exercise | null | undefined): boolean {
  return Boolean(exercise?.is_bodyweight)
}

/**
 * Does this exercise's weight field mean *assistance* — weight taken off?
 *
 * True for the assist machines, and for nothing else in the library. It is the
 * one place a bigger number is an easier set, which is why it has to be asked
 * before anything ranks, sums, or progresses a weight.
 */
export function weightIsAssist(exercise: Exercise | null | undefined): boolean {
  return Boolean(exercise?.is_assisted)
}

// The load arithmetic itself lives in lib/load.ts — it is pure, and this module
// pulls in Dexie, which a node:test run cannot import. Re-exported here so every
// caller still has one place to import from.
export { bodyweightFactor, movedWeightKg } from '../lib/load.ts'

export type OutboxOp = {
  op_id: string
  entity: 'session' | 'set' | 'metric'
  op: 'upsert' | 'delete'
  /** The row id this op targets — indexed so we can check "is this row dirty?" */
  row_id: string
  payload: Record<string, unknown>
  created_at: number
  attempts: number
  last_error?: string | null
}

export type MetaRow = { key: string; value: unknown }

// --- plans ------------------------------------------------------------------
// Server-authored and read-only on the client, so they ride the same full-
// refresh path as exercises rather than the seq cursor. Cached because starting
// a planned workout is a thing you do standing in a gym with no signal — the
// prescription has to be on the device before you get there.

export type PlanItem = {
  id: number
  exercise: { id: number; name: string } | null
  target_sets: number
  /** Both 0 when the exercise isn't counted in reps — a plank, say. */
  target_reps_low: number
  target_reps_high: number
  target_duration_seconds: number | null
  target_distance_m: number | null
  target_rpe: number | null
  /** The load the plan asks for. Present in the API response since the first
   *  migration; this type omitted it, so it was silently discarded on arrival. */
  target_weight_kg: number | null
  rest_seconds: number
  superset_group: string | null
  notes: string | null
}

export type PlanDay = { id: number; name: string; notes: string | null; items: PlanItem[] }

export type Plan = {
  id: number
  name: string
  description: string | null
  source: string
  weeks: number
  days_per_week: number
  progression: string | null
  days: PlanDay[]
}

// --- schema -----------------------------------------------------------------

export class LiftDB extends Dexie {
  sessions!: Table<Session, string>
  sets!: Table<SetRow, string>
  metrics!: Table<Metric, string>
  exercises!: Table<Exercise, number>
  plans!: Table<Plan, number>
  outbox!: Table<OutboxOp, string>
  meta!: Table<MetaRow, string>

  constructor() {
    super('lift')
    this.version(1).stores({
      sessions: 'id, started_at, deleted_at, seq',
      // [exercise_id+performed_at] is the compound index behind every progress
      // chart and every "what did I lift last time" lookup — the local mirror
      // of ix_sets_lookup on the server.
      sets: 'id, session_id, exercise_id, performed_at, [exercise_id+performed_at], deleted_at, seq',
      metrics: 'id, measured_at, deleted_at, seq',
      exercises: 'id, slug, name, category, equipment',
      outbox: 'op_id, row_id, created_at',
      meta: 'key',
    })

    // v2 adds plans. Only the new table is declared — Dexie carries the rest of
    // the schema forward, and an existing install upgrades in place without
    // touching a single logged set.
    this.version(2).stores({
      plans: 'id, name',
    })

    // v3 adds duration_seconds and distance_m to sets. Neither is indexed, so
    // there is no store string to change — the bump exists purely to mark the
    // shape change. Dexie stores are schemaless beyond their indexes, so the
    // new fields simply appear on rows that carry them.
    this.version(3).stores({})

    // v4 adds description to exercises. Nothing to index and nothing to
    // migrate — the exercise table is cleared and refilled from the server on
    // every full refresh, so the field simply arrives with the next sync.
    this.version(4).stores({})

    // v5 adds side to sets. Not indexed — rounds are grouped in memory from a
    // session's or an exercise's rows, which are already fetched by index — so
    // again the bump only marks the shape change.
    this.version(5).stores({})
  }
}

export const db = new LiftDB()

// --- meta helpers -----------------------------------------------------------

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row === undefined ? fallback : (row.value as T)
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

/** The sync cursor: the highest server `seq` this device has seen. */
export const CURSOR_KEY = 'sync.cursor'
/** Which user this local database belongs to — see wipeLocal(). */
export const OWNER_KEY = 'auth.user_id'

/**
 * Throw away everything local and start again from the server.
 *
 * Two callers, both important:
 *
 *  - The "Reset local data" button in Settings. Always available, always
 *    correct. Last-write-wins is simple enough to reason about that it will
 *    almost never be wrong, but when a user is convinced their phone is showing
 *    stale data, an escape hatch that costs twenty lines beats an afternoon of
 *    remote debugging.
 *
 *  - Sign-in as a different user. Without this, the second account would
 *    inherit the first one's sessions locally and then push them to the server
 *    under the wrong owner.
 */
export async function wipeLocal(): Promise<void> {
  await db.transaction('rw', db.sessions, db.sets, db.metrics, db.outbox, db.meta,
    async () => {
      await Promise.all([
        db.sessions.clear(),
        db.sets.clear(),
        db.metrics.clear(),
        db.outbox.clear(),
      ])
      await db.meta.delete(CURSOR_KEY)
    })
}
